import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import { api } from '../../services/api';

function fmtData(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}
function fmtReais(cents) {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}
function initials(nome) {
  return (nome ?? '?')
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

function StatusBadge({ status }) {
  const map = {
    CONFIRMED: {
      label: 'Confirmado',
      bg: '#dcfce7',
      color: '#166534',
      border: '#bbf7d0',
    },
    PENDING: {
      label: 'Pendente',
      bg: '#fef9c3',
      color: '#854d0e',
      border: '#fde68a',
    },
    CANCELLED: {
      label: 'Cancelado',
      bg: '#fee2e2',
      color: '#991b1b',
      border: '#fecaca',
    },
  };
  const s = map[status] ?? {
    label: status,
    bg: '#f1f5f9',
    color: '#64748b',
    border: '#e2e8f0',
  };
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 900,
        letterSpacing: '.06em',
        padding: '3px 9px',
        borderRadius: 99,
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
      }}
    >
      {s.label}
    </span>
  );
}

function Avatar({ nome, size = 32 }) {
  const colors = [
    '#ff2d6c',
    '#7c5cbf',
    '#0369a1',
    '#059669',
    '#d97706',
    '#be185d',
  ];
  const idx = (nome?.charCodeAt(0) ?? 0) % colors.length;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: colors[idx] + '22',
        border: `1.5px solid ${colors[idx]}44`,
        color: colors[idx],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.33,
        fontWeight: 900,
        flexShrink: 0,
      }}
    >
      {initials(nome)}
    </div>
  );
}

function ModalAlterarHorario({ inscricao, dias, onClose, onSuccess }) {
  const [novoHorarioId, setNovoHorarioId] = useState('');
  const [preview, setPreview] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const diasDisponiveis = dias.filter((d) => d.id !== inscricao.eventDayId);

  async function verPreview() {
    if (!novoHorarioId) {
      setErro('Selecione um horário.');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      const { data } = await api.patch(
        `/admin/inscricoes/${inscricao.id}/horario`,
        {
          eventDayId: novoHorarioId,
          enviarEmail: false,
        },
      );
      setPreview(data.preview);
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao buscar preview.');
    } finally {
      setSalvando(false);
    }
  }

  async function confirmarEnvio() {
    setSalvando(true);
    setErro('');
    try {
      const { data } = await api.patch(
        `/admin/inscricoes/${inscricao.id}/horario`,
        {
          eventDayId: novoHorarioId,
          enviarEmail: true,
        },
      );
      onSuccess(data.mensagem || 'Horário atualizado e comprovante enviado.');
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao atualizar horário.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.45)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 20,
          width: '100%',
          maxWidth: 520,
          boxShadow: '0 20px 60px rgba(0,0,0,.2)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ background: '#ff2d6c', padding: '20px 24px' }}>
          <div style={{ fontWeight: 900, fontSize: 16, color: '#fff' }}>
            Mudar Horário da Inscrição
          </div>
          <div style={{ fontSize: 13, color: '#ffd6e4', marginTop: 4 }}>
            {inscricao.nome} · {inscricao.email}
          </div>
        </div>

        <div style={{ padding: 24 }}>
          {/* Horário atual */}
          <div
            style={{
              marginBottom: 20,
              padding: '12px 16px',
              background: '#fff5f7',
              borderRadius: 12,
              border: '1px solid #fecdd3',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 900,
                color: '#94a3b8',
                textTransform: 'uppercase',
                marginBottom: 4,
              }}
            >
              Horário atual
            </div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
              {fmtData(inscricao.startDate)}
            </div>
          </div>

          {/* Seleção do novo horário */}
          {!preview && (
            <>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 900,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  marginBottom: 10,
                }}
              >
                Novo horário
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  marginBottom: 20,
                }}
              >
                {diasDisponiveis.length === 0 && (
                  <div style={{ color: '#94a3b8', fontSize: 13 }}>
                    Não há outros horários disponíveis neste evento.
                  </div>
                )}
                {diasDisponiveis.map((d) => {
                  const lotado = d.slots !== null && d.occupiedSlots >= d.slots;
                  return (
                    <label
                      key={d.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '12px 14px',
                        borderRadius: 10,
                        border: `2px solid ${novoHorarioId === d.id ? '#ff2d6c' : '#e2e8f0'}`,
                        background: novoHorarioId === d.id ? '#fff5f7' : '#fff',
                        cursor: lotado ? 'not-allowed' : 'pointer',
                        opacity: lotado ? 0.5 : 1,
                      }}
                    >
                      <input
                        type="radio"
                        name="horario"
                        value={d.id}
                        disabled={lotado}
                        checked={novoHorarioId === d.id}
                        onChange={() => setNovoHorarioId(d.id)}
                        style={{ accentColor: '#ff2d6c' }}
                      />
                      <div>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: '#0f172a',
                          }}
                        >
                          {fmtData(d.startDate)}
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>
                          {d.slots
                            ? `${d.occupiedSlots}/${d.slots} vagas`
                            : `${d.occupiedSlots} inscritas · ilimitado`}
                          {lotado && (
                            <span style={{ color: '#dc2626', marginLeft: 6 }}>
                              Lotado
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          )}

          {/* Preview do email */}
          {preview && (
            <div
              style={{
                marginBottom: 20,
                padding: '16px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: 12,
              }}
            >
              <div
                style={{
                  fontWeight: 900,
                  fontSize: 13,
                  color: '#166534',
                  marginBottom: 12,
                }}
              >
                Preview do comprovante
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  fontSize: 13,
                }}
              >
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ color: '#64748b', minWidth: 90 }}>Para:</span>
                  <strong>
                    {preview.nome} ({preview.email})
                  </strong>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ color: '#64748b', minWidth: 90 }}>
                    Evento:
                  </span>
                  <strong>{preview.evento}</strong>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ color: '#dc2626', minWidth: 90 }}>
                    Horário ant.:
                  </span>
                  <span
                    style={{ textDecoration: 'line-through', color: '#94a3b8' }}
                  >
                    {fmtData(preview.horarioAntigo)}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ color: '#166534', minWidth: 90 }}>
                    Novo horário:
                  </span>
                  <strong style={{ color: '#166534' }}>
                    {preview.horarioFormatado}
                  </strong>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ color: '#64748b', minWidth: 90 }}>Valor:</span>
                  <span>{preview.valor}</span>
                </div>
              </div>
            </div>
          )}

          {erro && (
            <div
              style={{
                color: '#dc2626',
                fontSize: 13,
                marginBottom: 12,
                fontWeight: 600,
              }}
            >
              {erro}
            </div>
          )}

          {/* Ações */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: 99,
                border: '1.5px solid #e2e8f0',
                background: '#fff',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                color: '#64748b',
              }}
            >
              Cancelar
            </button>
            {!preview ? (
              <button
                onClick={verPreview}
                disabled={!novoHorarioId || salvando}
                style={{
                  flex: 2,
                  padding: '12px',
                  borderRadius: 99,
                  background: '#0f172a',
                  color: '#fff',
                  border: 'none',
                  fontWeight: 900,
                  fontSize: 13,
                  cursor: 'pointer',
                  opacity: !novoHorarioId || salvando ? 0.5 : 1,
                }}
              >
                {salvando ? 'Verificando...' : 'Ver preview do email'}
              </button>
            ) : (
              <>
                <button
                  onClick={() => setPreview(null)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: 99,
                    border: '1.5px solid #e2e8f0',
                    background: '#fff',
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: 'pointer',
                    color: '#64748b',
                  }}
                >
                  Voltar
                </button>
                <button
                  onClick={confirmarEnvio}
                  disabled={salvando}
                  style={{
                    flex: 2,
                    padding: '12px',
                    borderRadius: 99,
                    background: '#ff2d6c',
                    color: '#fff',
                    border: 'none',
                    fontWeight: 900,
                    fontSize: 13,
                    cursor: 'pointer',
                    opacity: salvando ? 0.5 : 1,
                  }}
                >
                  {salvando ? 'Enviando...' : 'Confirmar e enviar'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 900,
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 600 }}>
        {value}
      </div>
    </div>
  );
}

function ActionButton({ onClick, disabled, color, bg, border, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        padding: '12px',
        borderRadius: 12,
        fontSize: 13,
        fontWeight: 700,
        color,
        background: bg,
        border: `1.5px solid ${border}`,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

/* Modal com todos os dados da inscrição + ações - a linha na lista mostra
   só nome/valor/status (ver HorarioCard), o resto mora aqui pra não
   espremer tudo numa linha só, principalmente no mobile. */
function ModalDetalhesInscricao({
  inscricao,
  todosOsDias,
  onClose,
  onEnviarComprovante,
  onAlterarHorario,
  onCancelar,
  onConfirmar,
  onSincronizar,
  enviando,
  sincronizando,
}) {
  const ins = inscricao;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.45)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 20,
          width: '100%',
          maxWidth: 440,
          maxHeight: '85vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,.2)',
        }}
      >
        <div
          style={{
            padding: '20px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            borderBottom: '1px solid #f1f5f9',
          }}
        >
          <Avatar nome={ins.nome} size={44} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontWeight: 900,
                fontSize: 16,
                color: '#0f172a',
                marginBottom: 4,
              }}
            >
              {ins.nome}
            </div>
            <StatusBadge status={ins.status} />
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar"
            style={{
              background: 'none',
              border: 'none',
              fontSize: 22,
              color: '#94a3b8',
              cursor: 'pointer',
              padding: 4,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            padding: '20px 24px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
          }}
        >
          <div style={{ gridColumn: '1 / -1' }}>
            <DetailRow label="E-mail" value={ins.email} />
          </div>
          <DetailRow label="CPF" value={ins.cpf} />
          <DetailRow label="WhatsApp" value={ins.whatsapp} />
          <DetailRow
            label="Tipo"
            value={ins.ticketType === 'MENSALISTA' ? 'Lily Member' : 'Avulso'}
          />
          <DetailRow
            label="Pagamento"
            value={
              ins.pagamento
                ? ins.pagamento.billingType === 'PIX'
                  ? 'PIX'
                  : 'Cartão'
                : '—'
            }
          />
          <DetailRow label="Valor" value={fmtReais(ins.amountInCents)} />
          <DetailRow label="Horário" value={fmtData(ins.startDate)} />
        </div>

        <div
          style={{
            padding: '4px 24px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {ins.status === 'PENDING' && (
            <ActionButton
              onClick={() => onConfirmar(ins.id, ins.nome)}
              color="#166534"
              bg="#f0fdf4"
              border="#bbf7d0"
            >
              ✓ Confirmar manualmente
            </ActionButton>
          )}
          {ins.status === 'PENDING' && ins.pagamento?.gatewayPaymentId && (
            <ActionButton
              onClick={() => onSincronizar(ins.id, ins.nome)}
              disabled={sincronizando === ins.id}
              color="#d97706"
              bg="#fffbeb"
              border="#fde68a"
            >
              {sincronizando === ins.id
                ? 'Verificando...'
                : '⟳ Sincronizar com Asaas'}
            </ActionButton>
          )}
          {todosOsDias.length > 1 && (
            <ActionButton
              onClick={() => onAlterarHorario(ins)}
              color="#7c3aed"
              bg="#f5f3ff"
              border="#ddd6fe"
            >
              🔄 Mudar horário
            </ActionButton>
          )}
          {ins.status === 'CONFIRMED' && (
            <ActionButton
              onClick={() => onEnviarComprovante(ins.id, ins.nome, ins.email)}
              disabled={enviando === ins.id}
              color="#2563eb"
              bg="#eff6ff"
              border="#bfdbfe"
            >
              {enviando === ins.id ? 'Enviando...' : '✉ Reenviar comprovante'}
            </ActionButton>
          )}
          {ins.status !== 'CANCELLED' && (
            <ActionButton
              onClick={() => onCancelar(ins.id, ins.nome)}
              color="#dc2626"
              bg="#fff5f5"
              border="#fecaca"
            >
              ✕ Cancelar inscrição
            </ActionButton>
          )}
        </div>
      </div>
    </div>
  );
}

function HorarioCard({
  dia,
  todosOsDias,
  onEnviarComprovante,
  onAlterarHorario,
  onCancelar,
  onConfirmar,
  onSincronizar,
  enviando,
  sincronizando,
}) {
  const [aberto, setAberto] = useState(false);
  const [detalhes, setDetalhes] = useState(null);
  const confirmadas = dia.inscricoes.filter(
    (i) => i.status === 'CONFIRMED',
  ).length;
  const pendentes = dia.inscricoes.filter((i) => i.status === 'PENDING').length;
  const ocupacao =
    dia.slots > 0 ? Math.round((dia.occupiedSlots / dia.slots) * 100) : 0;

  return (
    <>
      {detalhes && (
        <ModalDetalhesInscricao
          inscricao={detalhes}
          todosOsDias={todosOsDias}
          onClose={() => setDetalhes(null)}
          onEnviarComprovante={onEnviarComprovante}
          onAlterarHorario={(ins) => {
            setDetalhes(null);
            onAlterarHorario(ins);
          }}
          onCancelar={onCancelar}
          onConfirmar={onConfirmar}
          onSincronizar={onSincronizar}
          enviando={enviando}
          sincronizando={sincronizando}
        />
      )}
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          border: '1px solid #f1f5f9',
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,.04)',
        }}
      >
        <button
          onClick={() => setAberto((p) => !p)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            padding: '20px 24px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: '#fff0f5',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              border: '1.5px solid #fecdd3',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 900, color: '#ff2d6c' }}>
              {new Date(dia.startDate).toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                timeZone: 'UTC',
              })}
            </div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 900,
                color: '#ff2d6c',
                lineHeight: 1,
              }}
            >
              {new Date(dia.startDate).toLocaleString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'UTC',
              })}
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <div
              style={{
                fontWeight: 900,
                fontSize: 15,
                color: '#0f172a',
                marginBottom: 4,
              }}
            >
              {fmtData(dia.startDate)}
            </div>
            <div
              style={{
                display: 'flex',
                gap: 16,
                fontSize: 12,
                color: '#64748b',
              }}
            >
              <span>📋 {dia.inscricoes.length} inscrita(s)</span>
              <span style={{ color: '#166534' }}>
                ✓ {confirmadas} confirmada(s)
              </span>
              {pendentes > 0 && (
                <span style={{ color: '#854d0e' }}>
                  ⏳ {pendentes} pendente(s)
                </span>
              )}
              <span>
                🪑 {dia.slots ?? '∞'} vagas · {dia.occupiedSlots} ocupadas
              </span>
            </div>
          </div>

          <div style={{ width: 100, flexShrink: 0 }}>
            <div
              style={{
                fontSize: 11,
                color: '#94a3b8',
                marginBottom: 4,
                textAlign: 'right',
              }}
            >
              {ocupacao}% ocupado
            </div>
            <div style={{ height: 6, background: '#f1f5f9', borderRadius: 99 }}>
              <div
                style={{
                  height: '100%',
                  width: `${ocupacao}%`,
                  background:
                    ocupacao > 80
                      ? '#dc2626'
                      : ocupacao > 50
                        ? '#d97706'
                        : '#22c55e',
                  borderRadius: 99,
                }}
              />
            </div>
          </div>

          <span
            style={{
              fontSize: 20,
              color: '#cbd5e1',
              marginLeft: 8,
              transform: aberto ? 'rotate(180deg)' : 'none',
              display: 'inline-block',
              transition: 'transform .2s',
            }}
          >
            ▾
          </span>
        </button>

        {aberto && (
          <div style={{ borderTop: '1px solid #f8fafc' }}>
            {dia.inscricoes.length === 0 ? (
              <div
                style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: '#94a3b8',
                  fontSize: 13,
                }}
              >
                Nenhuma inscrição neste horário ainda.
              </div>
            ) : (
              <div style={{ padding: '0 0 8px' }}>
                {dia.inscricoes.map((ins, i) => (
                  <button
                    key={ins.id}
                    onClick={() =>
                      setDetalhes({
                        ...ins,
                        eventDayId: dia.id,
                        startDate: dia.startDate,
                      })
                    }
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      padding: '14px 24px',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      borderBottom:
                        i < dia.inscricoes.length - 1
                          ? '1px solid #f8fafc'
                          : 'none',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = '#fafafa')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = 'transparent')
                    }
                  >
                    <Avatar nome={ins.nome} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: '#0f172a',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {ins.nome}
                      </div>
                      <div
                        style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}
                      >
                        {fmtReais(ins.amountInCents)}
                      </div>
                    </div>
                    <StatusBadge status={ins.status} />
                    <span
                      style={{
                        color: '#cbd5e1',
                        fontSize: 16,
                        flexShrink: 0,
                      }}
                      aria-hidden="true"
                    >
                      ›
                    </span>
                  </button>
                ))}
              </div>
            )}
            <div
              style={{
                padding: '12px 24px',
                background: '#fafafa',
                borderTop: '1px solid #f1f5f9',
                display: 'flex',
                gap: 16,
                fontSize: 12,
                color: '#64748b',
              }}
            >
              <span>
                Receita confirmada:{' '}
                <strong style={{ color: '#0f172a' }}>
                  {fmtReais(
                    dia.inscricoes
                      .filter((i) => i.status === 'CONFIRMED')
                      .reduce((a, i) => a + i.amountInCents, 0),
                  )}
                </strong>
              </span>
              <span>
                Vagas livres:{' '}
                <strong
                  style={{
                    color:
                      (dia.slots ?? 999) - dia.occupiedSlots > 0
                        ? '#166534'
                        : '#991b1b',
                  }}
                >
                  {dia.slots !== null ? dia.slots - dia.occupiedSlots : '∞'}
                </strong>
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default function InscricoesPorEvento() {
  const { eventoId } = useParams();
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [enviando, setEnviando] = useState(null);
  const [sincronizando, setSincronizando] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [eventoSelecionado, setEventoSelecionado] = useState(eventoId || '');
  const [modalAlterarHorario, setModalAlterarHorario] = useState(null);
  const [sincronizandoTodos, setSincronizandoTodos] = useState(false);
  const [modalExportacao, setModalExportacao] = useState(null);
  const [ordemFiltro, setOrdemFiltro] = useState('alfabetico');

  useEffect(() => {
    api
      .get('/admin/eventos')
      .then(({ data }) => setEventos(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const id = eventoId || eventoSelecionado;
    if (!id) return;
    setCarregando(true);
    setDados(null);
    api
      .get(`/admin/eventos/${id}/inscricoes`)
      .then(({ data }) => setDados(data))
      .catch(() =>
        setFeedback({ msg: 'Erro ao carregar inscrições.', tipo: 'erro' }),
      )
      .finally(() => setCarregando(false));
  }, [eventoId, eventoSelecionado]);

  function mostrarFeedback(msg, tipo = 'ok') {
    setFeedback({ msg, tipo });
    setTimeout(() => setFeedback(null), 5000);
  }

  async function enviarComprovante(inscricaoId, nome, email) {
    if (!confirm(`Enviar comprovante para ${nome} (${email})?`)) return;
    setEnviando(inscricaoId);
    try {
      const { data } = await api.post(
        `/admin/inscricoes/${inscricaoId}/comprovante`,
      );
      mostrarFeedback(data.mensagem);
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao enviar comprovante.',
        'erro',
      );
    } finally {
      setEnviando(null);
    }
  }

  async function sincronizarInscricao(inscricaoId, nome) {
    setSincronizando(inscricaoId);
    try {
      const { data } = await api.post(
        `/admin/inscricoes/${inscricaoId}/sincronizar`,
      );
      if (data.atualizado) {
        mostrarFeedback(data.mensagem);
        const id = eventoId || eventoSelecionado;
        if (id)
          api
            .get(`/admin/eventos/${id}/inscricoes`)
            .then(({ data }) => setDados(data))
            .catch(() => {});
      } else {
        mostrarFeedback(`${nome}: ${data.mensagem}`, 'aviso');
      }
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao consultar Asaas.',
        'erro',
      );
    } finally {
      setSincronizando(null);
    }
  }

  async function confirmarInscricao(inscricaoId, nome) {
    if (
      !confirm(
        `Confirmar manualmente a inscrição de ${nome}? Isso marcará como paga e enviará o comprovante por email.`,
      )
    )
      return;
    try {
      const { data } = await api.post(
        `/admin/inscricoes/${inscricaoId}/confirmar`,
      );
      mostrarFeedback(data.mensagem);
      const id = eventoId || eventoSelecionado;
      if (id)
        api
          .get(`/admin/eventos/${id}/inscricoes`)
          .then(({ data }) => setDados(data))
          .catch(() => {});
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao confirmar inscrição.',
        'erro',
      );
    }
  }

  async function cancelarInscricao(inscricaoId, nome) {
    if (
      !confirm(
        `Cancelar inscrição de ${nome}? Esta ação não pode ser desfeita pelo sistema.`,
      )
    )
      return;
    try {
      await api.delete(`/admin/inscricoes/${inscricaoId}`);
      mostrarFeedback(`Inscrição de ${nome} cancelada.`);
      const id = eventoId || eventoSelecionado;
      if (id)
        api
          .get(`/admin/eventos/${id}/inscricoes`)
          .then(({ data }) => setDados(data))
          .catch(() => {});
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao cancelar inscrição.',
        'erro',
      );
    }
  }

  async function sincronizarTodosPendentes() {
    const id = eventoId || eventoSelecionado;
    if (!id) return;
    if (
      !confirm(
        'Consultar o Asaas para TODOS os pendentes deste evento e confirmar os que já foram pagos?',
      )
    )
      return;
    setSincronizandoTodos(true);
    try {
      const { data } = await api.post(
        `/admin/eventos/${id}/sincronizar-pendentes`,
      );
      const msg =
        data.mensagem +
        (data.confirmados > 0 ? ' Comprovantes enviados por email.' : '');
      mostrarFeedback(msg, data.confirmados > 0 ? 'ok' : 'aviso');
      if (data.confirmados > 0) {
        api
          .get(`/admin/eventos/${id}/inscricoes`)
          .then(({ data }) => setDados(data))
          .catch(() => {});
      }
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao sincronizar.',
        'erro',
      );
    } finally {
      setSincronizandoTodos(false);
    }
  }

  async function abrirExportacao() {
    const id = eventoId || eventoSelecionado;
    if (!id) {
      mostrarFeedback('Selecione um evento primeiro', 'erro');
      return;
    }
    try {
      // Usa o endpoint que já existe e funciona
      const { data } = await api.get(`/admin/eventos/${id}/inscricoes`);

      // Apenas CONFIRMADOS (pagos)
      const inscricoes = data.dias
        .flatMap((dia) => dia.inscricoes)
        .filter((i) => i.status === 'CONFIRMED')
        .map((i) => ({
          nomeCompleto: i.nome,
          cpf: i.cpf || '—',
          email: i.email,
          whatsapp: i.whatsapp || '—',
          status: i.status,
          tipoIngresso: i.ticketType,
        }));

      setModalExportacao({
        evento: data.evento,
        total: inscricoes.length,
        inscricoes,
      });
      setOrdemFiltro('alfabetico');
    } catch (err) {
      console.error('Erro:', err);
      mostrarFeedback('Erro ao carregar inscrições: ' + err.message, 'erro');
    }
  }

  function ordenarInscricoes(inscricoes, ordem) {
    const cópia = [...inscricoes];
    switch (ordem) {
      case 'alfabetico':
        return cópia.sort((a, b) =>
          a.nomeCompleto.localeCompare(b.nomeCompleto, 'pt-BR'),
        );
      case 'reverso':
        return cópia.sort((a, b) =>
          b.nomeCompleto.localeCompare(a.nomeCompleto, 'pt-BR'),
        );
      case 'recente':
        return cópia.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
      case 'antigo':
        return cópia.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        );
      default:
        return cópia;
    }
  }

  function exportarComoCsv() {
    if (!modalExportacao) return;
    const ordenadas = ordenarInscricoes(
      modalExportacao.inscricoes,
      ordemFiltro,
    );
    const lines = [
      'Nome Completo\tCPF\tEmail\tWhatsApp\tStatus\tTipo Ingresso',
      ...ordenadas.map(
        (i) =>
          `${i.nomeCompleto}\t${i.cpf}\t${i.email}\t${i.whatsapp}\t${i.status}\t${i.tipoIngresso}`,
      ),
    ];
    const csv = lines.join('\n');
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([bom, csv], {
      type: 'text/tab-separated-values;charset=utf-8;',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `inscricoes-${modalExportacao.evento.title.replace(/\s+/g, '_')}.tsv`;
    link.click();
  }

  function copiarParaAreaTransferencia() {
    if (!modalExportacao) return;
    const ordenadas = ordenarInscricoes(
      modalExportacao.inscricoes,
      ordemFiltro,
    );
    const texto = ordenadas
      .map((i) => `${i.nomeCompleto} | ${i.cpf} | ${i.email}`)
      .join('\n');
    navigator.clipboard.writeText(texto);
    mostrarFeedback('Copiado para a área de transferência!');
  }

  function handleAlterarHorarioSucesso(mensagem) {
    setModalAlterarHorario(null);
    mostrarFeedback(mensagem);
    // Recarrega os dados para refletir a nova distribuição de inscrições
    const id = eventoId || eventoSelecionado;
    if (id) {
      api
        .get(`/admin/eventos/${id}/inscricoes`)
        .then(({ data }) => setDados(data))
        .catch(() => {});
    }
  }

  const totalInscritas =
    dados?.dias?.reduce((a, d) => a + d.inscricoes.length, 0) ?? 0;
  const totalConfirmadas =
    dados?.dias?.reduce(
      (a, d) => a + d.inscricoes.filter((i) => i.status === 'CONFIRMED').length,
      0,
    ) ?? 0;
  const receitaTotal =
    dados?.dias?.reduce(
      (a, d) =>
        a +
        d.inscricoes
          .filter((i) => i.status === 'CONFIRMED')
          .reduce((b, i) => b + i.amountInCents, 0),
      0,
    ) ?? 0;

  return (
    <AdminLayout>
      {modalExportacao && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              width: '100%',
              maxWidth: 800,
              maxHeight: '80vh',
              boxShadow: '0 20px 60px rgba(0,0,0,.2)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div style={{ background: '#7c3aed', padding: '20px 24px' }}>
              <div
                style={{
                  fontWeight: 900,
                  fontSize: 18,
                  color: '#fff',
                  marginBottom: 4,
                }}
              >
                {modalExportacao.evento.title}
              </div>
              <div style={{ fontSize: 13, color: '#ddd6fe' }}>
                Total de inscrições: {modalExportacao.total}
              </div>
            </div>

            <div
              style={{
                padding: '16px 24px',
                borderBottom: '1px solid #e2e8f0',
                background: '#fafafa',
              }}
            >
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#64748b',
                  marginRight: 8,
                }}
              >
                Ordenar por:
              </label>
              <select
                value={ordemFiltro}
                onChange={(e) => setOrdemFiltro(e.target.value)}
                style={{
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid #e2e8f0',
                  fontWeight: 500,
                  fontSize: 13,
                  cursor: 'pointer',
                  background: '#fff',
                }}
              >
                <option value="alfabetico">A-Z (Alfabético)</option>
                <option value="reverso">Z-A (Reverso)</option>
                <option value="recente">Mais Recente</option>
                <option value="antigo">Mais Antigo</option>
              </select>
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
              <textarea
                readOnly
                value={ordenarInscricoes(
                  modalExportacao.inscricoes,
                  ordemFiltro,
                )
                  .map((i) => `${i.nomeCompleto} | ${i.cpf} | ${i.email}`)
                  .join('\n')}
                style={{
                  width: '100%',
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                  padding: '12px',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  color: '#0f172a',
                  resize: 'none',
                  fontWeight: 600,
                }}
              />
            </div>

            <div
              style={{
                padding: '16px 24px',
                background: '#fafafa',
                display: 'flex',
                gap: 10,
              }}
            >
              <button
                onClick={() => setModalExportacao(null)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 10,
                  border: '1.5px solid #e2e8f0',
                  background: '#fff',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                  color: '#64748b',
                }}
              >
                Fechar
              </button>
              <button
                onClick={copiarParaAreaTransferencia}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 10,
                  border: '1.5px solid #ddd6fe',
                  background: '#f5f3ff',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                  color: '#7c3aed',
                }}
              >
                📋 Copiar
              </button>
              <button
                onClick={exportarComoCsv}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 10,
                  background: '#7c3aed',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                  color: '#fff',
                  border: 'none',
                }}
              >
                ↓ Baixar CSV
              </button>
            </div>
          </div>
        </div>
      )}

      {modalAlterarHorario && (
        <ModalAlterarHorario
          inscricao={modalAlterarHorario}
          dias={dados?.dias ?? []}
          onClose={() => setModalAlterarHorario(null)}
          onSuccess={handleAlterarHorarioSucesso}
        />
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 24,
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <button
            onClick={() => navigate('/admin/eventos')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#94a3b8',
              fontSize: 13,
              padding: 0,
              marginBottom: 6,
            }}
          >
            ← Voltar para eventos
          </button>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 900,
              color: '#0f172a',
              margin: 0,
            }}
          >
            {dados?.evento?.title ?? 'Inscrições por Horário'}
          </h1>
          {dados && (
            <p style={{ fontSize: 13, color: '#94a3b8', margin: '4px 0 0' }}>
              {dados.dias?.length} horário(s) · {totalInscritas} inscritas
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!eventoId && (
            <select
              value={eventoSelecionado}
              onChange={(e) => setEventoSelecionado(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                fontSize: 13,
                color: '#0f172a',
                background: '#fff',
                cursor: 'pointer',
                minWidth: 240,
              }}
            >
              <option value="">Selecionar evento...</option>
              {eventos.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title}
                </option>
              ))}
            </select>
          )}
          {(eventoId || eventoSelecionado) && (
            <>
              <button
                onClick={abrirExportacao}
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#7c3aed',
                  background: '#f5f3ff',
                  border: '1.5px solid #ddd6fe',
                  borderRadius: 10,
                  padding: '10px 16px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                ↓ Exportar Inscrições
              </button>
              <button
                onClick={sincronizarTodosPendentes}
                disabled={sincronizandoTodos}
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#d97706',
                  background: '#fffbeb',
                  border: '1.5px solid #fde68a',
                  borderRadius: 10,
                  padding: '10px 16px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  opacity: sincronizandoTodos ? 0.6 : 1,
                }}
              >
                {sincronizandoTodos
                  ? '⟳ Verificando...'
                  : '⟳ Sincronizar pendentes'}
              </button>
            </>
          )}
        </div>
      </div>

      {feedback && (
        <div
          style={{
            padding: '12px 16px',
            borderRadius: 10,
            marginBottom: 20,
            fontSize: 13,
            fontWeight: 700,
            background:
              feedback.tipo === 'erro'
                ? '#fee2e2'
                : feedback.tipo === 'aviso'
                  ? '#fef3c7'
                  : '#dcfce7',
            color:
              feedback.tipo === 'erro'
                ? '#991b1b'
                : feedback.tipo === 'aviso'
                  ? '#92400e'
                  : '#166534',
          }}
        >
          {feedback.msg}
        </div>
      )}

      {dados && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
          {[
            {
              label: 'Total inscritas',
              value: totalInscritas,
              color: '#0f172a',
            },
            { label: 'Confirmadas', value: totalConfirmadas, color: '#166534' },
            {
              label: 'Pendentes',
              value: totalInscritas - totalConfirmadas,
              color: '#854d0e',
            },
            {
              label: 'Receita total',
              value: fmtReais(receitaTotal),
              color: '#7c3aed',
            },
          ].map((m) => (
            <div
              key={m.label}
              style={{
                background: '#fff',
                borderRadius: 12,
                padding: '14px 20px',
                border: '1px solid #f1f5f9',
                flex: 1,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: '#94a3b8',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '.06em',
                  marginBottom: 4,
                }}
              >
                {m.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 900, color: m.color }}>
                {m.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {carregando && (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>
          Carregando inscrições...
        </div>
      )}

      {!carregando && dados && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {dados.dias.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: 48,
                color: '#94a3b8',
                background: '#fff',
                borderRadius: 16,
                border: '1px solid #f1f5f9',
              }}
            >
              Nenhum horário cadastrado para este evento.
            </div>
          ) : (
            dados.dias.map((dia) => (
              <HorarioCard
                key={dia.id}
                dia={dia}
                todosOsDias={dados.dias}
                onEnviarComprovante={enviarComprovante}
                onAlterarHorario={setModalAlterarHorario}
                onConfirmar={confirmarInscricao}
                onCancelar={cancelarInscricao}
                onSincronizar={sincronizarInscricao}
                enviando={enviando}
                sincronizando={sincronizando}
              />
            ))
          )}
        </div>
      )}

      {!carregando && !dados && !eventoSelecionado && !eventoId && (
        <div
          style={{
            textAlign: 'center',
            padding: 64,
            color: '#94a3b8',
            background: '#fff',
            borderRadius: 16,
            border: '1px solid #f1f5f9',
          }}
        >
          <div style={{ fontSize: 36, marginBottom: 12 }}>◉</div>
          <p style={{ fontWeight: 700, fontSize: 15 }}>
            Selecione um evento acima para ver as inscrições.
          </p>
        </div>
      )}
    </AdminLayout>
  );
}
